'use client';

import { useState } from 'react';
import { GraphType, Period } from '../VaultGraphs';
import { VaultHistoryGraph } from './VaultHistoryGraph';
import { SHOW_VAULTS_ROI_GRAPH } from '../../../../constants/constants';
import { UiVaults } from '../../../../constants/vaults';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

const GRAPH_OPTIONS: { label: string; value: GraphType }[] = [
	{
		label: 'Share Price',
		value: 'sharePrice',
	},
	{
		label: 'Vault Balance',
		value: 'balance',
	},
];

if (SHOW_VAULTS_ROI_GRAPH) {
	GRAPH_OPTIONS.unshift({
		label: 'ROI',
		value: 'roi',
	});
}

const DEFAULT_GRAPH_OPTION = GRAPH_OPTIONS[0].value;

const PERIOD_OPTIONS: { label: string; value: Period }[] = [
	{
		label: '7D',
		value: '7d',
	},
	{
		label: '30D',
		value: '30d',
	},
	{
		label: '90D',
		value: '90d',
	},
];

export const VaultPerformanceBreakdownForSidebar = (props: {
	vaultPubkey: string;
	depositAssetMarketIndex: number;
}) => {
	const [selectedGraph, setSelectedGraph] = useState(DEFAULT_GRAPH_OPTION);
	const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_OPTIONS[0].value);

	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);
	const isExternalVault = !!uiVaultConfig?.externalUiLink;

	// Don't render chart for external vaults - we don't have snapshot data
	if (isExternalVault) {
		return null;
	}

	return (
		<div className="flex w-full flex-col gap-y-lg sm:rounded-xs">
			<div className="flex flex-wrap items-center justify-between gap-sm">
				<Tabs
					className="w-full"
					size="sm"
					variant="secondary"
					options={GRAPH_OPTIONS}
					activeTab={selectedGraph}
					onTabChange={(value) => setSelectedGraph(value)}
				/>
			</div>
			<VaultHistoryGraph
				vaultPubkey={props.vaultPubkey}
				depositAssetMarketIndex={props.depositAssetMarketIndex}
				graphType={selectedGraph}
				period={selectedPeriod}
			/>
			<Tabs
				className="w-full"
				size="sm"
				variant="secondary"
				options={PERIOD_OPTIONS}
				activeTab={selectedPeriod}
				onTabChange={(value) => setSelectedPeriod(value)}
			/>
		</div>
	);
};
export const VaultPerformanceBreakdownForViewVaultPage = (props: {
	vaultPubkey: string;
	depositAssetMarketIndex: number;
}) => {
	const [selectedGraph, setSelectedGraph] = useState(DEFAULT_GRAPH_OPTION);
	const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_OPTIONS[2].value);

	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);
	const isMobile = useIsMobileScreenSize();
	const isExternalVault = !!uiVaultConfig?.externalUiLink;

	// Don't render chart for external vaults - we don't have snapshot data
	if (isExternalVault) {
		return null;
	}

	return (
		<div className="flex flex-col gap-y-lg sm:mx-0 sm:w-full sm:rounded-xs">
			<div className="flex flex-wrap items-center justify-between gap-sm">
				<Tabs
					size="sm"
					className="w-full sm:w-fit"
					variant="secondary"
					options={GRAPH_OPTIONS}
					activeTab={selectedGraph}
					onTabChange={(value) => setSelectedGraph(value)}
				/>
				{!isMobile && (
					<Tabs
						size="sm"
						variant="secondary"
						options={PERIOD_OPTIONS}
						activeTab={selectedPeriod}
						onTabChange={(value) => setSelectedPeriod(value)}
					/>
				)}
			</div>

			<VaultHistoryGraph
				vaultPubkey={props.vaultPubkey}
				depositAssetMarketIndex={props.depositAssetMarketIndex}
				graphType={selectedGraph}
				period={selectedPeriod}
			/>
			{isMobile && (
				<Tabs
					size="sm"
					variant="secondary"
					options={PERIOD_OPTIONS}
					activeTab={selectedPeriod}
					onTabChange={(value) => setSelectedPeriod(value)}
				/>
			)}

			<div className="mt-sm">
				<span className="typo-body-2xs text-text-secondary">
					* ROI is based on the change in share price, not inclusive of fees.
				</span>
			</div>

			{uiVaultConfig?.isNotionalGrowthStrategy && (
				<div className="mt-sm">
					<span className="typo-body-2xs text-text-secondary">
						* Share Price calculated in notional terms for a Notional Growth
						Strategy Vault.
					</span>
				</div>
			)}
		</div>
	);
};
